import * as React from "react";

import { cn } from "./utils";

type InputSize = "regular" | "large" | "small" | "mini";

const sizeClasses: Record<InputSize, string> = {
  regular: "h-9 text-base",
  large: "h-11 text-base",
  small: "h-8 text-sm",
  mini: "h-7 text-xs",
};

interface InputProps extends React.ComponentProps<"input"> {
  decorationLeft?: React.ReactNode;
  decorationRight?: React.ReactNode;
  round?: boolean;
  inputSize?: InputSize;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, decorationLeft, decorationRight, round, inputSize = "regular", ...props }, ref) => {
    const sizeClass = sizeClasses[inputSize];

    // If no decorations, render simple input
    if (!decorationLeft && !decorationRight) {
      return (
        <input
          type={type}
          data-slot="input"
          className={cn(
            // Base styles
            "flex w-full min-w-0 bg-surface-L0 border border-border-L2 px-3 text-content-primary-bold leading-normal tracking-[0.07px] outline-none transition-[color,box-shadow,border-color]",
            // Size
            sizeClass,
            round ? "rounded-full" : "rounded-lg",
            // Placeholder
            "placeholder:text-content-primary-subtle",
            // Selection
            "selection:bg-background-primary-bold selection:text-content-primary-inverse",
            // Focus state
            "focus-visible:border-border-focus focus-visible:ring-[1px] focus-visible:ring-border-focus/50",
            // Error state
            "aria-invalid:border-border-negative-bold aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40",
            // Disabled state
            "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
            // File input
            "file:text-content-primary-bold file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium",
            // Dark mode
            "dark:bg-input/30",
            className,
          )}
          ref={ref}
          {...props}
        />
      );
    }

    // Render wrapper with decorations
    return (
      <div
        className={cn(
          // Base styles
          "flex w-full min-w-0 bg-surface-L0 border border-border-L2 items-center gap-2 px-3 transition-[color,box-shadow,border-color]",
          // Size
          sizeClass,
          round ? "rounded-full" : "rounded-lg",
          // Focus-within state (when input inside is focused)
          "focus-within:border-border-focus focus-within:ring-[1px] focus-within:ring-border-focus/50",
          // Error state
          "has-[input:invalid]:border-border-negative-bold has-[input:invalid]:ring-destructive/20 dark:has-[input:invalid]:ring-destructive/40",
          // Disabled state
          "has-[input:disabled]:pointer-events-none has-[input:disabled]:cursor-not-allowed has-[input:disabled]:opacity-50",
          // Dark mode
          "dark:bg-input/30",
          className,
        )}
      >
        {decorationLeft && (
          <div className="shrink-0 flex items-center text-content-primary-subtle">
            {decorationLeft}
          </div>
        )}
        <input
          type={type}
          data-slot="input"
          className={cn(
            // Base styles
            "flex-1 min-w-0 bg-transparent text-left text-content-primary-bold leading-normal tracking-[0.07px] outline-none border-0",
            sizeClass,
            // Placeholder
            "placeholder:text-content-primary-subtle",
            // Selection
            "selection:bg-background-primary-bold selection:text-content-primary-inverse",
            // File input
            "file:text-content-primary-bold file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium",
          )}
          ref={ref}
          {...props}
        />
        {decorationRight && (
          <div className="shrink-0 flex items-center text-content-primary-subtle">
            {decorationRight}
          </div>
        )}
      </div>
    );
  }
);
Input.displayName = "Input";

export { Input };
export type { InputSize };
